import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import { LoadingButton } from '@backoffice-frontend/forms'
import {
  FccAdAlertType,
  FccTargetDoorStatus,
} from '@backoffice-frontend/graphql'
import {
  LinearProgress,
  MoiaLabelCheckbox,
} from '@backoffice-frontend/patterns'
import { Callout, Text } from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdVehicleDoorStatus } from '../../../AdVehicleDoorStatus/useAdVehicleDoorStatus'
import { PassengerCount } from '../../PassengerCount/PassengerCount'
import { getSeatingCounts } from '../../seating'
import { TelematicsCheck } from '../../TelematicsCheck'
import { ActionCard } from '../ActionCard'
import {
  type AdVehicleStreamCabinSafetyCheckVehicleFragment,
  useAdVehicleStreamConfirmCabinSafetyMutation,
} from './CabinSafetyCheck.hook'
import { DoorStateCheck } from './DoorStateCheck'

type CabinSafetyCheckProps = {
  onSuccess: VoidFunction
  vehicle: AdVehicleStreamCabinSafetyCheckVehicleFragment
  expectedPassengerCount: number
}

export const CabinSafetyCheck = ({
  vehicle,
  onSuccess,
  expectedPassengerCount,
}: CabinSafetyCheckProps) => {
  const { t } = useTranslation()
  const [overwriteTelematics, setOverwriteTelematics] = useState(false)

  const {
    handleDoorRequest,
    requestedDoorStatus,
    currentDoorStatus,
    errors,
    isRequestPending,
  } = useAdVehicleDoorStatus()

  const [confirmCabinSafety, { loading: isConfirmingCabinSafety }] =
    useErrorHandlingMutation(useAdVehicleStreamConfirmCabinSafetyMutation, {
      variables: { vehicleId: vehicle.id },
    })

  const { seated: seatedPassengerCount, buckledUp: buckledUpPassengerCount } =
    getSeatingCounts(vehicle)

  const [hasConfirmedLessPassengers, setHasConfirmedLessPassengers] =
    useState(false)

  const isAllPassengersSeated = seatedPassengerCount === expectedPassengerCount

  const isAllSeatedPassengersBuckledUp =
    expectedPassengerCount > 0
      ? seatedPassengerCount > 0 &&
        seatedPassengerCount === buckledUpPassengerCount
      : buckledUpPassengerCount === 0

  const isAllTelematicsChecksConfirmed =
    ((isAllPassengersSeated && isAllSeatedPassengersBuckledUp) ||
      hasConfirmedLessPassengers) &&
    currentDoorStatus === FccTargetDoorStatus.Closed &&
    requestedDoorStatus === undefined

  const isReadyToConfirmCabinSafety =
    isAllTelematicsChecksConfirmed || overwriteTelematics

  return (
    <ActionCard headline={t('adFleetMap_cscGiveClearanceForDeparture_header')}>
      {hasCabinSafetyCheckAlert(vehicle) ? (
        <div
          css={theme => ({
            height: '100%',
            padding: theme.space.Space4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.space.Space4,
          })}
        >
          <div
            css={theme => ({
              flexGrow: 1,
              alignSelf: 'stretch',
              display: 'flex',
              gap: theme.space.Space4,
            })}
          >
            <div
              css={theme => ({
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space4,
              })}
            >
              <PassengerCount
                expectedPassengerCount={expectedPassengerCount}
                seatedPassengerCount={seatedPassengerCount}
                onConfirmLessPassengers={() =>
                  setHasConfirmedLessPassengers(true)
                }
                hasConfirmedLessPassengers={hasConfirmedLessPassengers}
              />

              {expectedPassengerCount === 0 && (
                <TelematicsCheck
                  title={
                    seatedPassengerCount === 0
                      ? t('adFleetMap_cscAllSeatsEmpty_caption')
                      : t('adFleetMap_passengersStillSeated_label')
                  }
                  variant={
                    seatedPassengerCount === 0
                      ? 'success'
                      : overwriteTelematics
                        ? 'overwrite'
                        : 'pending'
                  }
                />
              )}
              {expectedPassengerCount > 0 && (
                <TelematicsCheck
                  title={
                    isAllSeatedPassengersBuckledUp
                      ? t('adFleetMap_cscAllPassengersBuckledUp_caption')
                      : t('adFleetMap_unbuckledPassengers_label')
                  }
                  variant={
                    isAllSeatedPassengersBuckledUp
                      ? 'success'
                      : overwriteTelematics
                        ? 'overwrite'
                        : 'pending'
                  }
                />
              )}

              <DoorStateCheck
                errors={errors}
                currentDoorStatus={currentDoorStatus}
                pendingDoorActuation={requestedDoorStatus}
                handleDoorActuation={handleDoorRequest}
                mutationLoading={isRequestPending}
              />
            </div>
          </div>
          {!isReadyToConfirmCabinSafety && (
            <div css={{ width: '60%' }}>
              <Callout hideDismiss variant="neutral">
                {t('adFleetMap_waitForTelematicsCheck_caption')}
              </Callout>
            </div>
          )}
          {isReadyToConfirmCabinSafety && (
            <>
              <Text size="lg">
                {t('adFleetMap_cscEverythingLooksFine_caption')}
              </Text>
              <LoadingButton
                variant="primary"
                loading={isConfirmingCabinSafety}
                onClick={async () => {
                  const { errors } = await confirmCabinSafety()
                  if (!errors) {
                    onSuccess()
                  }
                }}
                label={t('adFleetMap_cscGiveClearance_button')}
              />
            </>
          )}
          <MoiaLabelCheckbox
            checked={overwriteTelematics}
            onChange={() => {
              setOverwriteTelematics(value => !value)
            }}
            label={t('map_skipTelematics_caption')}
          />
        </div>
      ) : (
        <LinearProgress />
      )}
    </ActionCard>
  )
}

const hasCabinSafetyCheckAlert = (
  vehicle: AdVehicleStreamCabinSafetyCheckVehicleFragment,
): boolean => {
  return (
    vehicle.fccCurrentAdAlerts?.alerts.some(
      alert => alert.type === FccAdAlertType.CabinSafetyCheck,
    ) || false
  )
}
